(ns app.front.view.public-notice.documents
  (:require [app.domain.attachment.file-formats :as file-formats]
            [app.domain.committee.model :as committee-model]
            [app.domain.public-notice.model :as public-notice-model]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.file.download :as file-download]
            [app.front.file.upload :as upload]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(def new-uploaded-doc-id "new-public-notice-document")


(rf/reg-sub ::documents
  :<- [::entities/context]
  (fn [ctx [_ parent-id]]
    (->> (entities/find-entities ctx {:entity/type   :entity.type/public-notice-document
                                      :entity/parent parent-id})
         (sort-by :entity/created))))


(rf/reg-sub ::documents-visible?
  (fn [[_ target-id] _]
    [(rf/subscribe [::documents target-id])
     (rf/subscribe [:app.front.process/signal-permitted? target-id [:process.signal/add-public-notice-document]])])
  (fn [[documents add-permitted?] _]
    (or (seq documents)
        add-permitted?)))


(rf/reg-event-fx ::file-uploaded
  (fn [_ [_ status response]]
    (let [[file-id] (first response)]
      {:dispatch-n [[::entities/merge-entity-db status response]
                    [::ee/set-values
                     new-uploaded-doc-id
                     {:public-notice-document/description ""
                      :public-notice-document/type        :public-notice-document.type/note
                      :public-notice-document/visibility  :public-notice-document.visibility/everyone
                      :file-id                            file-id}]]})))


(defn- add-document [fsm-target]
  (let [doc-data (get (<sub [::ee/unsaved]) new-uploaded-doc-id)]
    (>evt [:app.front.process/signal
           fsm-target
           [:process.signal/add-public-notice-document doc-data]
           {:on-success [::ee/cancel-changes new-uploaded-doc-id]
            :notify     {:success :public-notice/edit-success}}])))


(defn- edit-document [fsm-target doc-id]
  (let [doc-data (get (<sub [::ee/unsaved]) doc-id)]
    (>evt [:app.front.process/signal
           fsm-target
           [:process.signal/edit-public-notice-document (assoc doc-data :id doc-id)]
           {:on-success [::ee/cancel-changes doc-id]
            :notify     {:success :public-notice/edit-success}}])))


(defn- permitted? [fsm-target signal-id]
  (<sub [::process/signal-permitted? fsm-target [signal-id]]))


(defn visibility-as-kw [document]
  (let [perms (set (:entity/required-read-permissions document))]
    (if (contains? perms :permission.public-notice/authority-read)
      :public-notice-document.visibility/authorities
      :public-notice-document.visibility/everyone)))


(defn- document-details-editor [{:keys [tr fsm-target document]}]
  (let [doc-id      (or (:db/id document) new-uploaded-doc-id)
        adding-new? (-> document :db/id nil?)
        editing?    (<sub [::ee/has-changes? doc-id])
        description (<sub [::ee/attr-value doc-id :public-notice-document/description])
        doc-type    (<sub [::ee/attr-value doc-id :public-notice-document/type])
        visibility  (or (<sub [::ee/attr-value doc-id :public-notice-document/visibility])
                        (visibility-as-kw document))
        file-id     (or (<sub [::ee/attr-value doc-id :file-id])
                        (-> document
                            :entity/_parent
                            (p/find-first {:entity/type :entity.type/file})
                            :db/id))
        file        (<sub [::entities/entity file-id])
        class       (futil/cs (if adding-new? "border-ui-yellow" "border-ui-green")
                              (if editing? "lg:grid-cols-5 md:grid-cols-2" "lg:grid-cols-8 xl:grid-cols-7")
                              "items-center grid-cols-1")]
    [:li.grid.gap-3.gap-y-2.lg:gap-y-0.p-4.mb-4.bg-ui-light-gray.border-l-4.border-solid.break-all {:class class}
     (if editing?
       [:div.bx--form-item
        [:div.bx--label
         (tr [:file/filename])]
        [:div.h-10.flex.items-center
         (:file/filename file)]]
       [file-download/download-workspace-file-link (select-keys file [:file/filename :db/id])])
     (if editing?
       [ui-select/select
        {:id           (str "public-notice-type-" doc-id)
         :label-text   (tr [:public-notice-document/type])
         :value        doc-type
         :class-name   "full-width-select"
         :values       public-notice-model/document-types
         :val->str     #(tr [%])
         :allow-empty? false
         :on-change    #(>evt [::ee/set-value doc-id :public-notice-document/type %])}]
       (tr [doc-type]))
     [:div.lg:col-span-2.break-words
      (if editing?
        [ui/text-input
         {:id         (str "public-notice-desc-" doc-id)
          :label-text (tr [:public-notice-document/description])
          :light      true
          :value      description
          :on-change  #(rf/dispatch-sync
                         [::ee/set-value doc-id :public-notice-document/description (futil/tval %)])}]
        description)]
     (if editing?
       [ui-select/select
        {:id           (str "public-notice-visibility-" doc-id)
         :label-text   (tr [:public-notice-document/visibility])
         :value        visibility
         :class-name   "full-width-select"
         :values       public-notice-model/visibility
         :val->str     #(tr [%])
         :allow-empty? false
         :on-change    #(>evt [::ee/set-value doc-id :public-notice-document/visibility %])}]
       [:div.break-words
        [:p.font-bold (tr [:public-notice/visibility-label])]
        (tr [visibility])])

     ;; Buttons & link
     (if editing?
       [:<>
        [:div.md:col-span2.lg:col-span-4]
        [:div.flex.justify-end.h-full.lg:items-end.mt-3
         (cc/button
           {:kind     :ghost
            :size     :small
            :icon     i/remove
            :on-click #(>evt [::ee/cancel-changes doc-id])}
           (tr [:cancel]))
         (cc/button
           {:kind     :primary
            :size     :small
            :icon     i/check
            :on-click #(if adding-new?
                         (add-document fsm-target)
                         (edit-document fsm-target doc-id))}
           (tr [:save]))]]
       [:<>
        [:div.lg:col-span-3.xl:col-span-2.flex.justify-end.flex-wrap
         (when (permitted? fsm-target :process.signal/edit-public-notice-document)
           (cc/button
             {:kind     :ghost
              :icon     i/pen
              :size     :small
              :on-click #(>evt [::ee/set-values
                                doc-id
                                (merge (select-keys document [:public-notice-document/description
                                                              :public-notice-document/type])
                                       {:public-notice-document/visibility visibility})])}
             (tr [:edit])))
         (when (permitted? fsm-target :process.signal/delete-public-notice-document)
           [ui/modal-confirmation-button
            {:tr                  tr
             :id                  (str "public-notice-document-remove-" doc-id)
             :modal-title         (tr [:public-notice/confirm-remove])
             :on-yes-click        #(>evt [:app.front.process/signal
                                          fsm-target
                                          [:process.signal/delete-public-notice-document {:id doc-id}]
                                          {:notify {:success :public-notice/delete-success}}])
             :trigger-button-opts {:kind       :ghost
                                   :icon       i/trash
                                   :size       :small
                                   :class-name "ml-2"}
             :trigger-button-text (tr [:remove])}])]])]))


(defn- document-list [{:keys [documents tr fsm-target creating-new?] :as opts}]
  [:ul
   (futil/for-frag [document documents]
     [document-details-editor (assoc opts :document document)])
   (when creating-new?
     [document-details-editor (util/mmap tr fsm-target)])])


(defn download-public-notice-letter [tr workspace]
  (r/with-let [*selected-committee-item (r/atom nil)]
    (let [committee-items  (:committee-item/_item workspace)
          single-option?   (-> committee-items count (= 1))
          selected-item    (if single-option?
                             (first committee-items)
                             @*selected-committee-item)
          lang             (<sub [:language])
          format-date-time (<sub [::date-util/date-time-fn])]
      (when (seq committee-items)
        [:div.flex.flex-col.md:flex-row.items-start.md:items-center.mt-4.lg:mt-0
         (when-not single-option?
           [:div.mb-4.lg:mb-0
            [ui-select/select {:id          "public-notice-document-committee-item-select"
                               :select-text (tr [:public-notice/select-meeting])
                               :label-text  ""
                               :hide-label  true
                               :value       @*selected-committee-item
                               :on-change   #(reset! *selected-committee-item %)
                               :val->str    #(str
                                               (-> % :entity/parent :entity/parent (committee-model/committee-name lang))
                                               " "
                                               (-> % :entity/parent :committee-meeting/date format-date-time))
                               :values      committee-items}]])
         (cc/button
           {:icon             i/download
            :kind             :ghost
            :icon-description ""
            :disabled         (nil? selected-item)
            :href             (file-download/download-href :public-notice/download-document
                                                           {:committee-item-id (:db/id selected-item)})}
           (tr [:public-notice/download-letter]))]))))


(defn section [{:keys [fsm-target workspace tr]}]
  (when (<sub [::documents-visible? (:db/id fsm-target)])
    (let [creating-new? (<sub [::ee/has-changes? new-uploaded-doc-id])
          can-upload?   (and (permitted? fsm-target :process.signal/add-public-notice-document)
                             (not creating-new?))
          documents     (<sub [::documents (:db/id fsm-target)])
          pending?      (<sub [::upload/workspace-files-pending?])]
      [:div
       [:div.flex-col.lg:flex-row.flex.justify-between.flex-nowrap.items-start.lg:items-center.mb-4
        [:h3.m-0.flex-1 (tr [:public-notice/documents])]
        [download-public-notice-letter tr workspace]
        (cond
          pending?
          [:div
           (cc/inline-loading {:description (tr [:file/processing])
                               :success     (not pending?)})]

          can-upload?
          [upload/upload-button
           (str new-uploaded-doc-id "-file")
           {:attach-file-fn  (partial upload/add-workspace-file
                                      fsm-target
                                      [::file-uploaded])
            :button-i18n-key :public-notice/upload
            :button-opts     {:kind :ghost}
            :accept-types    file-formats/accept-value-for-input}])]
       [document-list (util/mmap tr workspace fsm-target documents creating-new?)]])))
